import { Box, CircularProgress, Fade } from "@mui/material";
import { WebBackButton } from "../../components/shared";
import { useNavigate } from "react-router-dom";
import { useTelegram } from "../../hooks";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  getReferralAccruals,
  getReferralOverview,
  purchasePremium,
} from "../../services/referrals";
import { HeroBalanceCard } from "../../components/pages/referral/hero-balance-card";
import { StatsStrip } from "../../components/pages/referral/stats-strip";
import { TiersCarousel } from "../../components/pages/referral/tiers-carousel";
import { RulesCard } from "../../components/pages/referral/rules-card";
import { InviteCard } from "../../components/pages/referral/invite-card";
import { RewardsList } from "../../components/pages/referral/rewards-list";
import { QrDrawer } from "../../components/pages/referral/qr-drawer";
import { PremiumDrawer } from "../../components/pages/referral/premium-drawer";
import { useTranslation } from "react-i18next";

export function ReferralPage() {
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const { t } = useTranslation("referral");

  const [qrOpen, setQrOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);

  const overviewQuery = useQuery(
    ["referral", "overview"],
    getReferralOverview,
    { staleTime: 30_000 },
  );
  const accrualsQuery = useQuery(
    ["referral", "accruals", 1],
    () => getReferralAccruals(1, 15),
    { staleTime: 30_000 },
  );

  const purchaseMutation = useMutation(purchasePremium, {
    onSuccess: () => {
      enqueueSnackbar(t("premium.activated"), { variant: "success" });
      setPremiumOpen(false);
      qc.invalidateQueries(["referral"]);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t("premium.activation_failed");
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  useEffect(() => {
    tg.BackButton.show();
    const handler = () => navigate("/profile");
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  const overview = overviewQuery.data;

  const premiumTier = useMemo(
    () => overview?.tiers.find((t) => t.is_premium) ?? null,
    [overview],
  );

  const standardTier = useMemo(
    () => overview?.tiers.find((t) => t.code === "LVL1") ?? null,
    [overview],
  );

  // Следующий не-премиум уровень для прогресс-бара в hero-карточке.
  const nextTier = useMemo(() => {
    if (!overview) return null;
    const sorted = [...overview.tiers]
      .filter((t) => !t.is_premium)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((t) => t.code === overview.tier.code);
    if (idx < 0 || idx >= sorted.length - 1) return null;
    return sorted[idx + 1];
  }, [overview]);

  const handleWithdraw = () => {
    navigate("/withdrawl", {
      state: {
        balance: overview?.account.balance ?? "0",
        min_payout: overview?.settings.min_payout_usdt ?? "10",
        networks: overview?.settings.payout_networks ?? ["TRC20"],
      },
    });
  };

  const handleShare = () => {
    const link = overview?.referral_link ?? "";
    const text = t("invite.share_message");
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
    tg.openTelegramLink(shareUrl);
  };

  if (overviewQuery.isLoading || !overview) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fade in>
      <Box
        sx={{
          marginBottom: "100px",
          width: "100%",
          maxWidth: "460px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <WebBackButton to="/profile" title={t("title")} />

        <HeroBalanceCard
          balance={overview.account.balance}
          minPayoutUsdt={overview.settings.min_payout_usdt}
          currentTier={overview.tier}
          nextTier={nextTier}
          referralsTotal={overview.referrals_total}
          isPremium={overview.account.is_premium}
          onWithdraw={handleWithdraw}
        />

        <StatsStrip
          referralsCount={overview.referrals_total}
          totalEarned={overview.account.total_earned}
          totalWithdrawn={overview.account.total_withdrawn}
        />

        <TiersCarousel
          tiers={overview.tiers}
          currentTierCode={overview.tier.code}
          isPremium={overview.account.is_premium}
          referralsTotal={overview.referrals_total}
          onPremiumClick={() => setPremiumOpen(true)}
        />

        <RulesCard
          tier={overview.tier}
          isPremium={overview.account.is_premium}
        />

        <InviteCard
          referralLink={overview.referral_link}
          onOpenQr={() => setQrOpen(true)}
          onShare={handleShare}
        />

        <RewardsList
          items={accrualsQuery.data?.items ?? []}
          isLoading={accrualsQuery.isLoading}
        />

        <QrDrawer
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          link={overview.referral_link}
          code={overview.referral_code}
        />

        <PremiumDrawer
          open={premiumOpen}
          onClose={() => setPremiumOpen(false)}
          premiumTier={premiumTier}
          standardTier={standardTier ?? overview.tier}
          onPurchase={() => purchaseMutation.mutate()}
          isPurchasing={purchaseMutation.isLoading}
          isAlreadyPremium={overview.account.is_premium}
        />
      </Box>
    </Fade>
  );
}
